/**
 * Editor для doc.loyalty_transactions (Порція Роздріб.5b).
 *
 * Race-safe accrual/writeoff:
 *  - backend через FOR UPDATE на customer_cards
 *  - тут тільки UI: lookup по картка_code + клієнт-сайд preview балансу
 *
 * reason dropdown — динамічно за transaction_type:
 *  - accrual: manual_bonus / birthday_bonus / promo
 *  - writeoff: expiry / manual_correction / fraud_block
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailLoyaltyTransactions,
  type LoyaltyTransactionType,
} from '@/api/retailLoyaltyTransactions';
import { retailCustomerCards, type CustomerCardLookup } from '@/api/retailCustomerCards';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

const REASONS_BY_TYPE: Record<LoyaltyTransactionType, { value: string; label: string }[]> = {
  accrual: [
    { value: 'manual_bonus', label: 'Ручне нарахування' },
    { value: 'birthday_bonus', label: 'День народження' },
    { value: 'promo', label: 'Промо/акція' },
  ],
  writeoff: [
    { value: 'expiry', label: 'Прострочено' },
    { value: 'manual_correction', label: 'Ручна корекція' },
    { value: 'fraud_block', label: 'Блокування (підозра)' },
  ],
};

const PERMISSION_BY_TYPE: Record<LoyaltyTransactionType, string> = {
  accrual: 'retail.loyalty.accrue',
  writeoff: 'retail.loyalty.writeoff',
};

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  transaction_type: LoyaltyTransactionType;
  customer_card_id: string;
  card_code: string; // для lookup
  amount: string;
  reason: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  transaction_type: 'accrual',
  customer_card_id: '',
  card_code: '',
  amount: '0',
  reason: 'manual_bonus',
  comment: '',
};

export default function LoyaltyTransactionEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  // У create — потрібна accrual ABO writeoff permission; беремо
  // ширший customer_card.manage для list-orgs (backend пропустить).
  const { availableOrgs } = usePermissionsByOrg('retail.customer_card.manage');
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [foundCard, setFoundCard] = useState<CustomerCardLookup | null>(null);

  const docQuery = useQuery({
    queryKey: ['retail-loyalty-transaction', id],
    queryFn: () => retailLoyaltyTransactions.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  const numberPreview = useNumberPreview(
    'doc.loyalty_transactions',
    form.organization_id,
    isNew,
  );

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        transaction_type: d.transaction_type,
        customer_card_id: d.customer_card_id,
        card_code: '',
        amount: d.amount,
        reason: d.reason,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  // При зміні transaction_type — скидаємо reason на дефолт.
  useEffect(() => {
    if (isNew) {
      const first = REASONS_BY_TYPE[form.transaction_type][0];
      if (first && !REASONS_BY_TYPE[form.transaction_type].some((r) => r.value === form.reason)) {
        setForm((f) => ({ ...f, reason: first.value }));
      }
    }
  }, [form.transaction_type, form.reason, isNew]);

  const lookupCard = async () => {
    setError(null);
    setFoundCard(null);
    if (!form.organization_id || !form.card_code) {
      setError('Заповніть код картки');
      return;
    }
    try {
      const c = await retailCustomerCards.lookup({
        organization_id: form.organization_id,
        card_code: form.card_code,
      });
      setFoundCard(c);
      setForm((f) => ({ ...f, customer_card_id: c.id }));
    } catch (e) {
      setError(extractApiError(e));
    }
  };

  const posted = !!docQuery.data?.posted;
  const balanceBefore = foundCard
    ? Number(foundCard.bonus_balance)
    : docQuery.data
      ? Number(docQuery.data.balance_before)
      : null;
  const balancePreview =
    balanceBefore !== null && form.amount
      ? form.transaction_type === 'accrual'
        ? balanceBefore + Number(form.amount)
        : balanceBefore - Number(form.amount)
      : null;

  const writeoffExceeds =
    form.transaction_type === 'writeoff' &&
    balanceBefore !== null &&
    form.amount &&
    Number(form.amount) > balanceBefore;

  const create = useMutation({
    mutationFn: () =>
      retailLoyaltyTransactions.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        transaction_type: form.transaction_type,
        customer_card_id: form.customer_card_id,
        amount: form.amount,
        reason: form.reason,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-loyalty-transactions'] });
      navigate(`/retail/loyalty-transactions/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () => retailLoyaltyTransactions.update(id!, { comment: form.comment || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-loyalty-transactions'] });
      qc.invalidateQueries({ queryKey: ['retail-loyalty-transaction', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailLoyaltyTransactions.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-loyalty-transactions'] });
      navigate('/retail/loyalty-transactions');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        transaction_type: form.transaction_type,
        customer_card_id: form.customer_card_id,
        amount: form.amount,
        reason: form.reason,
      },
      {
        organization_id: 'Юрособа',
        transaction_type: 'Тип',
        customer_card_id: 'Картка',
        amount: 'Сума',
        reason: 'Причина',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Нова бонусна транзакція ${numberPreview ? `(${numberPreview})` : ''}`
            : `Бонусна транзакція ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: 'var(--badge-green-fg)', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/loyalty-transactions')}>Скасувати</button>
          {!posted && (
            <RequirePermission code={PERMISSION_BY_TYPE[form.transaction_type]}>
              <button
                className="primary"
                onClick={handleSave}
                disabled={create.isPending || update.isPending || writeoffExceeds === true}
                title={writeoffExceeds ? 'Сума перевищує бонусний баланс картки' : undefined}
              >
                Зберегти
              </button>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code={PERMISSION_BY_TYPE[form.transaction_type]}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити транзакцію?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.code}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Дата транзакції</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={posted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Тип транзакції</label>
          <select
            value={form.transaction_type}
            onChange={(e) =>
              setForm({ ...form, transaction_type: e.target.value as LoyaltyTransactionType })
            }
            disabled={!isNew}
          >
            <option value="accrual">+ Нарахування</option>
            <option value="writeoff">− Списання</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Причина</RequiredLabel>
          <select
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            disabled={posted}
          >
            {REASONS_BY_TYPE[form.transaction_type].map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        {isNew && (
          <div className="field" style={{ gridColumn: 'span 12' }}>
            <label>Код картки</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                value={form.card_code}
                onChange={(e) => setForm({ ...form, card_code: e.target.value })}
                placeholder="Введіть номер картки"
                style={{ flex: 1 }}
              />
              <button onClick={lookupCard}>Знайти</button>
            </div>
            {foundCard && (
              <small style={{ color: 'var(--badge-green-fg)', display: 'block', marginTop: '0.25rem' }}>
                ✓ {foundCard.holder_name ?? '(без імені)'} • поточний баланс:{' '}
                {Number(foundCard.bonus_balance).toLocaleString('uk-UA', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                бонусів
              </small>
            )}
          </div>
        )}
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Сума</RequiredLabel>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            disabled={posted}
          />
          {balancePreview !== null && (
            <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '0.25rem' }}>
              Баланс після: {balancePreview.toFixed(2)}
            </small>
          )}
          {writeoffExceeds && (
            <small style={{ color: 'var(--badge-red-fg)', display: 'block', marginTop: '0.25rem' }}>
              ⚠ Сума перевищує доступний баланс ({balanceBefore?.toFixed(2)})
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
